// Single source of truth for all page copy.
// Keeping content here keeps the components about layout and behaviour only.

export const doctor = {
  name: "Dr. Aakash Sharma",
  credential: "BPT, MPT",
  role: "Physiotherapist & Manual Therapist",
  email: "consult@draakashsharma.in",
  phone: "+91 85957 38618",
  phoneHref: "+918595738618",
  whatsapp: "918595738618",
  location: "Gurugram, Haryana",

  /* ---------------------------------------------------------------
     ADDRESS — fill these in before launch.
     Every field left empty is omitted from the LocalBusiness schema
     rather than published as a guess. Google will not rank a local
     business without a real, consistent street address, and the
     address here must match your Google Business Profile exactly
     (same spelling, same abbreviations) or the citation will not
     be trusted.
     --------------------------------------------------------------- */
  address: {
    streetAddress: "", // e.g. "Unit 214, Vipul Trade Centre, Sohna Road"
    locality: "Gurugram",
    region: "Haryana",
    postalCode: "", // e.g. "122018"
    country: "IN",
  },

  /* Latitude/longitude of the clinic. Right-click the exact pin in
     Google Maps to copy them. Omitted from schema when blank. */
  geo: { lat: "", lng: "" },

  /* Your Google Business Profile URL — the single highest-impact
     local-SEO asset. Also Practo / Justdial listings. */
  profiles: {
    google: "",
    practo: "",
    justdial: "",
    instagram: "",
  },
};

/* Localities served — these are the phrases people actually search
   ("physiotherapist in DLF Phase 3"). They appear as real page copy
   AND as areaServed in the LocalBusiness schema. */
export const areasServed = [
  "DLF Phase 1",
  "DLF Phase 2",
  "DLF Phase 3",
  "DLF Phase 4",
  "DLF Phase 5",
  "Golf Course Road",
  "Golf Course Extension Road",
  "Sohna Road",
  "Sushant Lok",
  "Cyber City",
  "Sector 56",
  "Sector 57",
  "MG Road",
  "Nirvana Country",
  "South City 1 & 2",
  "Palam Vihar",
];

/* Top-level navigation. `children` renders a dropdown; the parent
   is itself a real page, so it stays clickable rather than being a
   label that goes nowhere. Blogs is injected at runtime only when
   posts exist — see Masthead. */
export const routes = [
  { path: "/physiotherapy", label: "Physiotherapy", hasDropdown: true },
  { path: "/osteopathy-chiropractic", label: "Osteopathy & Chiropractic" },
  { path: "/oriental-medicine", label: "Oriental Medicine" },
  { path: "/fitness-programs", label: "Fitness Programs" },
  { path: "/blog", label: "Blogs", requiresPosts: true },
];

/* The quick-identification grid on the homepage: what a patient
   actually types or thinks, mapped to where it is treated. */
export const conditionsGrid = [
  { label: "Back Pain", icon: "spine", to: "/conditions#low-back-pain" },
  { label: "Neck Pain", icon: "joint", to: "/conditions#cervical-pain" },
  { label: "Sciatica", icon: "neuro", to: "/osteopathy-chiropractic#back-neck-sciatica-relief" },
  { label: "Knee & Joint Pain", icon: "joint", to: "/physiotherapy/orthopedic-sports-physiotherapy" },
  { label: "Frozen Shoulder", icon: "joint", to: "/physiotherapy/orthopedic-sports-physiotherapy" },
  { label: "Sports Injury", icon: "hiit", to: "/physiotherapy/orthopedic-sports-physiotherapy" },
  { label: "Post-Surgery Recovery", icon: "surgical", to: "/physiotherapy/pre-post-surgical-rehabilitation" },
  { label: "Stroke Rehabilitation", icon: "neuro", to: "/physiotherapy/neurological-rehabilitation" },
  { label: "Vertigo & Dizziness", icon: "vestibular", to: "/physiotherapy/vestibular-rehabilitation" },
  { label: "Desk Posture Pain", icon: "posture", to: "/physiotherapy/posture-ergonomic-correction" },
  { label: "Arthritis", icon: "geriatric", to: "/physiotherapy/geriatric-physiotherapy" },
  { label: "Balance & Falls", icon: "taichi", to: "/physiotherapy/geriatric-physiotherapy" },
];

/* Homepage "Why choose us" — the practice's actual differentiators. */
export const whyChooseUs = {
  eyebrow: "Our approach",
  heading: "Assessment first. Always.",
  lead: "Four things that shape how every case here is handled.",
  points: [
    {
      icon: "posture",
      title: "The diagnosis comes before the treatment",
      text: "Every first visit is a full movement, postural and orthopedic assessment. Nothing is treated until it is clear what is producing it.",
    },
    {
      icon: "iastm",
      title: "Hands on, then hands off",
      text: "Manual therapy relieves the symptom. Graded loading is what stops it returning. You always leave with a programme.",
    },
    {
      icon: "taichi",
      title: "Two traditions, one plan",
      text: "Clinical rehabilitation and Oriental medicine are not rivals here. Whichever moves you forward is what you get.",
    },
    {
      icon: "strength",
      title: "Progress you can measure",
      text: "Range, strength and pain are recorded at each visit, so improvement is a number rather than a feeling.",
    },
  ],
};

export const hero = {
  eyebrow: "Physiotherapist in Gurugram",
  headlineLead: "Pain has a cause.",
  headlineEmphasis: "Most of the work is finding it.",
  body:
    "I treat orthopedic, neurological and sports injuries across Gurugram — combining clinical rehabilitation with osteopathy, acupuncture and manual therapy. Clinic and home visits. Every plan starts with an assessment, not an assumption.",
  focus: [
    { label: "01 / Clinical", text: "Orthopedic, sports & neurological rehabilitation" },
    { label: "02 / Manual", text: "Osteopathy, chiropractic care & soft tissue work" },
    { label: "03 / Oriental", text: "Acupuncture, moxibustion & craniosacral therapy" },
  ],
};

export const about = {
  eyebrow: "About",
  heading: "The assessment comes first.",
  paragraphs: [
    "I trained in physiotherapy because the work is diagnostic before it is manual. A stiff neck is rarely just a stiff neck — it is a jaw, a desk, an old ankle sprain that quietly changed how you stand.",
    "My job is to trace the pain back to whatever is actually producing it, treat that, and then hand you a plan you can carry out without me. Relief in the room matters. What you can do six weeks later matters more.",
  ],
  quote:
    "You should be able to explain your own injury by the time you leave the first visit.",
  principles: [
    {
      title: "Assess before you treat",
      text: "Every first session is a full movement, postural and orthopedic assessment. No two plans come out the same.",
    },
    {
      title: "Hands on, then hands off",
      text: "Manual therapy relieves the symptom. Graded loading and training is what stops it from coming back.",
    },
    {
      title: "Two traditions, one plan",
      text: "Clinical rehabilitation and Oriental medicine are not rivals here. Whichever moves you forward is what you get.",
    },
    {
      title: "Progress you can measure",
      text: "Range, strength and pain are recorded at each visit, so improvement is a number rather than a feeling.",
    },
  ],
};

export const treatments = {
  eyebrow: "Treatments & Therapies",
  heading: "Four areas of clinical practice.",
  intro:
    "The core of the practice. Each begins with the same assessment and diverges into the protocol the findings call for.",
  items: [
    {
      icon: "joint",
      title: "Orthopedic & Sports Physiotherapy",
      text: "Joint, muscle and tendon injuries — from sprains and frozen shoulder to the overuse patterns that come with training. Assessment, manual therapy and a graded return to load.",
      tags: ["Sprains & strains", "Frozen shoulder", "Tendinopathy", "Return to sport"],
    },
    {
      icon: "neuro",
      title: "Neurological Physiotherapy",
      text: "Rehabilitation after stroke, nerve injury and neurological conditions. Retraining balance, gait and motor control at the pace the nervous system will actually allow.",
      tags: ["Post-stroke", "Gait & balance", "Nerve injury", "Motor control"],
    },
    {
      icon: "surgical",
      title: "Pre & Post Surgical Rehabilitation",
      text: "Preparing joints and muscles before an operation, and rebuilding range, strength and confidence after it. Protocol-led and matched to your surgeon's timeline.",
      tags: ["Prehabilitation", "Post-op protocol", "Scar & range", "Strength rebuild"],
    },
    {
      icon: "spine",
      title: "Osteopathy & Chiropractic Care",
      text: "Structural assessment and manual correction across the spine, pelvis and peripheral joints — applied where the examination says it is warranted, not by routine.",
      tags: ["Spinal mobilisation", "Pelvic alignment", "Joint manipulation", "Myofascial release"],
    },
  ],
};

export const recovery = {
  eyebrow: "Advanced Recovery",
  heading: "Techniques that open a region before it is loaded.",
  intro:
    "Adjuncts, not substitutes. Each is used to make the tissue ready for the rehabilitation that follows it.",
  items: [
    {
      icon: "cupping",
      title: "Cupping",
      text: "Negative-pressure suction applied over restricted tissue to lift fascia, decompress the layer beneath it and improve local circulation. Used to release a stubborn region before it is trained again.",
    },
    {
      icon: "needle",
      title: "Acupuncture",
      text: "Fine needles placed at specific points to settle pain and release trigger points. Applied both within its classical framework and as dry needling for myofascial referral patterns.",
    },
    {
      icon: "iastm",
      title: "IASTM",
      text: "Instrument-assisted soft tissue mobilisation. A contoured steel tool reads adhesion and scar tissue the hand can pass over, then works it down along the line of the fibres.",
    },
  ],
};

export const conditions = {
  eyebrow: "Conditions Treated",
  heading: "What people usually arrive with.",
  intro:
    "Most cases fall into three groups. Open one to see what it covers.",
  groups: [
    {
      title: "Cervical Pain",
      summary: "Neck, shoulder and referred arm pain",
      items: [
        "Neck stiffness and restricted rotation",
        "Cervical spondylosis and disc-related pain",
        "Cervicogenic headaches and jaw involvement",
        "Radiating pain, tingling or numbness into the arm",
        "Desk and screen posture syndromes",
        "Whiplash and post-traumatic neck pain",
      ],
    },
    {
      title: "Low Back Pain",
      summary: "Lumbar, pelvic and sciatic presentations",
      items: [
        "Disc bulge, herniation and sciatic referral",
        "Sacroiliac joint dysfunction",
        "Lumbar spondylosis and facet joint pain",
        "Postural and sedentary lower back pain",
        "Lifting injuries and acute muscle spasm",
        "Postnatal and pelvic girdle pain",
      ],
    },
    {
      title: "General Physiotherapy Conditions",
      summary: "Musculoskeletal, neurological and sports-related",
      items: [
        "Musculoskeletal — arthritis, bursitis, tendinitis, plantar fasciitis, joint stiffness",
        "Neurological — stroke recovery, facial palsy, peripheral neuropathy, balance disorders",
        "Sports — ACL and meniscus rehabilitation, ankle sprains, shin splints, tennis and golfer's elbow",
        "Post-fracture and post-immobilisation stiffness",
        "Work-related repetitive strain injuries",
        "Geriatric mobility, fall prevention and gait retraining",
      ],
    },
  ],
};

export const training = {
  eyebrow: "Rehabilitation Phase 3",
  heading: "Return to load.",
  intro:
    "The final phase of treatment, not a separate gym service. Once pain settles and range returns, the tissue has to be reloaded — or the injury comes back. Every modality below is prescribed against your reassessment findings and progressed on measured criteria.",
  items: [
    {
      icon: "bodyweight",
      phase: "Reload",
      title: "Graded Strength",
      text: "Progressive bodyweight and resistance loading of the healed structure, advanced only when range and pain criteria are met.",
    },
    {
      icon: "functional",
      phase: "Reload",
      title: "Functional Retraining",
      text: "Rebuilding the specific movements your work and day demand — lifting, stairs, overhead reach, sit-to-stand.",
    },
    {
      icon: "yoga",
      phase: "Mobility",
      title: "Therapeutic Movement",
      text: "Yoga-derived mobility and stability sequencing, adapted around your restrictions rather than a class format.",
    },
    {
      icon: "taichi",
      phase: "Balance",
      title: "Balance & Proprioception",
      text: "Tai Chi-based weight-shifting for joint position sense and fall prevention — core to geriatric and post-stroke work.",
    },
    {
      icon: "qigong",
      phase: "Recovery",
      title: "Breath & Recovery Work",
      text: "Qigong-derived breath-led movement to down-regulate the nervous system in persistent-pain presentations.",
    },
    {
      icon: "cardio",
      phase: "Capacity",
      title: "Cardiovascular Conditioning",
      text: "Structured aerobic work to rebuild the endurance lost during immobilisation, dosed to your tolerance.",
    },
    {
      icon: "hiit",
      phase: "Capacity",
      title: "Interval Conditioning",
      text: "Short high-intensity intervals for return-to-sport cases only, once full load tolerance is established.",
    },
  ],
};

export const oriental = {
  eyebrow: "Oriental & Alternative Medicine",
  heading: "The older half of the practice.",
  intro:
    "Traditions that read the body as a whole system rather than a set of parts. Offered alongside clinical rehabilitation — never in place of a diagnosis.",
  items: [
    {
      icon: "aroma",
      title: "Aromatherapy",
      text: "Therapeutic essential oils applied through massage and inhalation to ease muscular tension, sleep disturbance and stress-driven pain.",
      origin: "Botanical",
    },
    {
      icon: "needle",
      title: "Acupuncture",
      text: "Needling along classical meridians to regulate pain signalling and restore circulation to a restricted region.",
      origin: "Classical Chinese",
    },
    {
      icon: "moxa",
      title: "Moxa / Moxibustion",
      text: "Warmth from smouldering mugwort held over a point or channel — used for cold, stiff, chronic presentations that resist manual work alone.",
      origin: "Classical Chinese",
    },
    {
      icon: "cranio",
      title: "Craniosacral Therapy",
      text: "Very light sustained contact at the skull, spine and sacrum to settle the craniosacral rhythm and release deep protective tension.",
      origin: "Osteopathic",
    },
    {
      icon: "microcurrent",
      title: "Microcurrent Therapy",
      text: "Sub-sensory electrical current matched to the body's own signalling to accelerate tissue repair and reduce inflammatory pain.",
      origin: "Bioelectric",
    },
  ],
};

/* Answers marked NEEDS-CONFIRMATION are business policy I cannot
   invent — verify each before launch. They are written as safe,
   non-committal truths so nothing false ships if you miss one. */
export const faqs = [
  {
    q: "Do I need a doctor's referral to book physiotherapy?",
    a: "No. You can book a physiotherapy assessment directly — a referral is not required in India. If you already have a prescription, scan or MRI report, bring it, because it shortens the assessment. If anything in your presentation needs a specialist opinion, I will tell you and refer you on.",
  },
  {
    q: "How many sessions will I need?",
    a: "It depends on what is actually causing the problem, which is what the first assessment establishes. A simple mechanical neck or back issue often settles in 4–6 sessions. Post-surgical and neurological rehabilitation runs longer and follows the surgeon's protocol. You will get an estimated range at the end of the first visit, and it is reviewed as your measurements change — not open-ended.",
  },
  {
    q: "Do you offer home visits in Gurgaon?",
    a: "Yes. Home visits are available across Gurugram and are the usual choice for post-surgical, post-stroke and reduced-mobility cases where travelling is difficult. Tell me your locality when you request an appointment and I will confirm availability and any travel time.",
  },
  {
    q: "What happens in the first session?",
    a: "About sixty minutes: history, a full movement and orthopedic assessment, an explanation of what the findings mean, your first treatment, and a written home programme you leave with. You should be able to explain your own injury by the time you go.",
  },
  {
    q: "Is acupuncture or dry needling painful?",
    a: "The needles are far finer than injection needles and most people feel a dull ache or a brief twitch rather than sharp pain. It is always explained and consented to first, and it is never the only thing in a treatment plan — it is used to make a restricted area ready for the rehabilitation that follows.",
  },
  {
    q: "What should I wear to an appointment?",
    a: "Loose, comfortable clothing you can move in. For a knee or ankle problem bring shorts; for a shoulder or neck problem a vest or sleeveless top helps. The assessment needs to see the joint move.",
  },
  {
    q: "Do you treat sports injuries and return-to-sport cases?",
    a: "Yes — ACL and meniscus rehabilitation, ankle sprains, shin splints, tennis and golfer's elbow, and the overuse patterns that come with running and gym training. Return to sport is criteria-based: you progress when strength and control measurements allow it, not on a fixed date.",
  },
  {
    q: "What are your fees?",
    // NEEDS-CONFIRMATION — replace with your actual fee range.
    a: "Fees depend on whether the session is at the clinic or at your home, and on the length of the plan. Message me on WhatsApp and I will give you the exact figure before you book — there are no surprise charges.",
  },
];

export const booking = {
  title: "Request an appointment",
  note: "Fill this in and it opens a ready-made message in WhatsApp. I confirm every slot personally — usually within a few hours, same day if it's before 7pm.",
  privacy:
    "Your details go straight to me on WhatsApp or email. Nothing is stored on this website and nothing is shared.",
  concerns: [
    "Neck / cervical pain",
    "Low back pain or sciatica",
    "Shoulder, knee or joint pain",
    "Sports injury",
    "Post-surgical rehabilitation",
    "Neurological (stroke, nerve, balance)",
    "Post-fracture stiffness",
    "Not sure — need an assessment",
  ],
  visitTypes: ["Clinic visit", "Home visit"],
  slots: [
    "Morning (9:00 – 12:00)",
    "Afternoon (12:00 – 16:00)",
    "Evening (16:00 – 19:00)",
  ],
};

export const contact = {
  eyebrow: "Consultation",
  heading: "Start with an assessment.",
  intro:
    "A first visit runs about sixty minutes: history, full physical assessment, first treatment, and the plan you leave with.",
  hours: [
    { day: "Monday – Friday", time: "9:00 — 19:00" },
    { day: "Saturday", time: "9:00 — 14:00" },
    { day: "Sunday", time: "Closed" },
  ],
  note: "Home visits available for post-surgical and neurological cases.",
};
